import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetRequest, PatchRequest } from "../../../../api/Request";
import { StoreAnalyticsDetailResponse } from "../storeAnalytics.types";
import { formatDate } from "../../../../util/util";
import { BuyApprovalStatusEnum } from "../../../../enum";
import { useBuyApproval } from "../online/useBuyApproval";
import {
  buyStoreBaseUrl,
  formatCurrency as formatRoundedCurrency,
  formatDateTime,
  formatNumber,
  getBuyState,
  isOnBuy,
} from "../online/onlineStore.util";
import "../styles/storeAnalyticsDetail.scss";

const StoreAnalyticsDetail: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<StoreAnalyticsDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (storeId) {
      fetchStoreDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchStoreDetail = async () => {
    if (!storeId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await GetRequest(
        `admin/v1/store/${storeId}/analytics`
      );
      setData(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch store details");
    } finally {
      setLoading(false);
    }
  };

  const { confirmApprove, openReject, isReviewSaving, rejectModal } =
    useBuyApproval(() => fetchStoreDetail());

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const handleUpdateSubscription = () => {
    navigate(`/store/${storeId}/subscription/update`);
  };

  const handleCancelSubscription = async () => {
    if (!storeId) return;

    const reason = prompt("Please provide a reason for cancellation:");
    if (!reason) return;

    try {
      await PatchRequest(
        `admin/v1/store/${storeId}/subscription/cancel`,
        { reason }
      );
      alert("Subscription cancelled successfully");
      fetchStoreDetail();
    } catch (err: any) {
      alert(err.message || "Failed to cancel subscription");
    }
  };

  if (loading) {
    return (
      <div className="store-analytics-detail">
        <div className="store-analytics-detail__loading">
          Loading store details...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="store-analytics-detail">
        <div className="store-analytics-detail__error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { store, analytics, onlineStats } = data;
  const approvalStatus = store.buyConfig?.approvalStatus;
  const buyState = getBuyState(store.buyConfig);
  const bannerList = store.imageList || [];

  return (
    <div className="store-analytics-detail">
      <div className="store-analytics-detail__header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="store-title">
          {store.profileImage && (
            <img className="store-logo" src={store.profileImage} alt={store.name} />
          )}
          <div>
            <h1>{store.name}</h1>
            <div className="store-id">ID: {store._id}</div>
          </div>
        </div>
      </div>

      <div className="store-analytics-detail__content">
        <div className="store-analytics-detail__card">
          <h2>Store Information</h2>
          <div className="store-analytics-detail__info-grid">
            <div className="store-analytics-detail__info-item">
              <div className="label">Created At</div>
              <div className="value">{store.createdAt ? formatDate(store.createdAt) : "N/A"}</div>
            </div>

            <div className="store-analytics-detail__info-item">
              <div className="label">Phone Number</div>
              <div className="value">
                {store.phoneNumberList[0]
                  ? store.phoneNumberList[0].phoneNumber
                  : "N/A"}
              </div>
            </div>

            <div className="store-analytics-detail__info-item">
              <div className="label">Email</div>
              <div className="value">{store.email || "N/A"}</div>
            </div>

            {store.address && (
              <>
                <div className="store-analytics-detail__info-item" style={{ gridColumn: '1 / -1' }}>
                  <div className="label">Address</div>
                  <div className="value">
                    {store.address.line1 && <div>{store.address.line1}</div>}
                    {store.address.locality && <div>{store.address.locality}</div>}
                    <div>
                      {[store.address.city, store.address.district, store.address.state, store.address.postalCode]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    {store.address.country && <div>{store.address.country}</div>}
                  </div>
                </div>

                {store.location?.coordinates && (
                  <div className="store-analytics-detail__info-item">
                    <div className="label">Location</div>
                    <div className="value">
                      <a
                        href={`https://www.google.com/maps?q=${store.location.coordinates[1]},${store.location.coordinates[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}

            {store.typeList && store.typeList.length > 0 && (
              <div className="store-analytics-detail__info-item" style={{ gridColumn: '1 / -1' }}>
                <div className="label">Type List</div>
                <div className="value">
                  {store.typeList.join(", ")}
                </div>
              </div>
            )}

            {store.categoryList && store.categoryList.length > 0 && (
              <div className="store-analytics-detail__info-item" style={{ gridColumn: '1 / -1' }}>
                <div className="label">Category List</div>
                <div className="value">
                  {store.categoryList.join(", ")}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="store-analytics-detail__card">
          <h2>Subscription Details</h2>
          {store.subscription ? (
            <>
              <div className="store-analytics-detail__info-grid">
                <div className="store-analytics-detail__info-item">
                  <div className="label">Type</div>
                  <div className="value">
                    <span
                      className={`value--badge ${store.subscription.type.toLowerCase()}`}
                    >
                      {store.subscription.type}
                    </span>
                  </div>
                </div>

                <div className="store-analytics-detail__info-item">
                  <div className="label">Status</div>
                  <div className="value">
                    <span
                      className={`value--badge ${store.isExpired ? "expired" : "active"}`}
                    >
                      {store.isExpired ? "Expired" : "Active"}
                    </span>
                  </div>
                </div>

                <div className="store-analytics-detail__info-item">
                  <div className="label">Plan</div>
                  <div className="value">{store.subscription.plan || "N/A"}</div>
                </div>

            <div className="store-analytics-detail__info-item">
              <div className="label">Amount</div>
              <div className="value">
                {store.subscription.amount
                  ? formatCurrency(store.subscription.amount)
                  : "N/A"}
              </div>
            </div>

            <div className="store-analytics-detail__info-item">
              <div className="label">Start Date</div>
              <div className="value">
                {store.subscription.startDate ? formatDate(store.subscription.startDate) : "N/A"}
              </div>
            </div>

            <div className="store-analytics-detail__info-item">
              <div className="label">Expiry Date</div>
              <div className="value">
                {store.subscription.expiryDate ? formatDate(store.subscription.expiryDate) : "N/A"}
              </div>
            </div>

            {store.subscription.remarks && (
              <div
                className="store-analytics-detail__info-item"
                style={{ gridColumn: "1 / -1" }}
              >
                <div className="label">Remarks</div>
                <div className="value">{store.subscription.remarks}</div>
              </div>
            )}
          </div>

          <div className="store-analytics-detail__actions">
            <button
              className="update-btn"
              onClick={handleUpdateSubscription}
            >
              Update Subscription
            </button>
            {store.subscription.isActive && (
              <button
                className="cancel-btn"
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </button>
            )}
          </div>
          </>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
              <p>No subscription information available for this store.</p>
              <div className="store-analytics-detail__actions">
                <button
                  className="update-btn"
                  onClick={handleUpdateSubscription}
                >
                  Create Subscription
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="store-analytics-detail__card store-analytics-detail__card--full">
          <h2>Online (BUY)</h2>
          {isOnBuy(store.buyConfig) ? (
            <>
              <div className="store-analytics-detail__info-grid">
                <div className="store-analytics-detail__info-item">
                  <div className="label">Status</div>
                  <div className="value">
                    <span className={`value--badge ${buyState.className}`}>
                      {buyState.label}
                    </span>
                  </div>
                </div>

                <div className="store-analytics-detail__info-item">
                  <div className="label">BUY Link</div>
                  <div className="value">
                    {store.slug ? (
                      <a
                        href={`${buyStoreBaseUrl}${store.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#007bff", textDecoration: "underline" }}
                      >
                        modulusbuy.com/store/{store.slug}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </div>
                </div>

                <div className="store-analytics-detail__info-item">
                  <div className="label">Applied On</div>
                  <div className="value">
                    {formatDateTime(store.buyConfig?.appliedAt)}
                  </div>
                </div>

                <div className="store-analytics-detail__info-item">
                  <div className="label">Reviewed On</div>
                  <div className="value">
                    {formatDateTime(store.buyConfig?.reviewedAt)}
                  </div>
                </div>

                {approvalStatus === BuyApprovalStatusEnum.Rejected &&
                  store.buyConfig?.rejectionReason && (
                    <div
                      className="store-analytics-detail__info-item"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <div className="label">Rejection Reason</div>
                      <div className="value">{store.buyConfig.rejectionReason}</div>
                    </div>
                  )}

                <div
                  className="store-analytics-detail__info-item"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <div className="label">Banners ({bannerList.length})</div>
                  <div className="value">
                    {bannerList.length > 0 ? (
                      <div className="buy-banners">
                        {bannerList.map((image, index) => (
                          <a key={image} href={image} target="_blank" rel="noopener noreferrer">
                            <img src={image} alt={`Banner ${index + 1}`} />
                          </a>
                        ))}
                      </div>
                    ) : (
                      "No banners"
                    )}
                  </div>
                </div>
              </div>

              {onlineStats && (
                <div className="store-analytics-detail__metrics store-analytics-detail__metrics--spaced">
                  <div className="store-analytics-detail__metric">
                    <div className="metric-label">Online Orders</div>
                    <div className="metric-value">
                      {formatNumber(onlineStats.onlineOrderCount)}
                    </div>
                    <div className="metric-subtitle">
                      {formatNumber(onlineStats.recentOnlineOrderCount)} in last 30 days
                    </div>
                  </div>

                  <div className="store-analytics-detail__metric store-analytics-detail__metric--success">
                    <div className="metric-label">Online Business</div>
                    <div className="metric-value">
                      {formatRoundedCurrency(onlineStats.onlineRevenue)}
                    </div>
                    <div className="metric-subtitle">
                      {formatRoundedCurrency(onlineStats.recentOnlineRevenue)} in last 30 days
                    </div>
                  </div>

                  <div className="store-analytics-detail__metric">
                    <div className="metric-label">Average Order</div>
                    <div className="metric-value">
                      {formatRoundedCurrency(onlineStats.averageOrderValue)}
                    </div>
                    <div className="metric-subtitle">Per paid online order</div>
                  </div>

                  <div className="store-analytics-detail__metric store-analytics-detail__metric--success">
                    <div className="metric-label">Delivered</div>
                    <div className="metric-value">
                      {formatNumber(onlineStats.deliveredOrderCount)}
                    </div>
                    <div className="metric-subtitle">Online orders delivered</div>
                  </div>

                  <div className="store-analytics-detail__metric store-analytics-detail__metric--warning">
                    <div className="metric-label">Cancelled</div>
                    <div className="metric-value">
                      {formatNumber(onlineStats.cancelledOrderCount)}
                    </div>
                    <div className="metric-subtitle">Paid orders cancelled</div>
                  </div>

                  <div className="store-analytics-detail__metric">
                    <div className="metric-label">Last Online Order</div>
                    <div className="metric-value" style={{ fontSize: "20px" }}>
                      {formatDateTime(onlineStats.lastOnlineOrderAt)}
                    </div>
                    <div className="metric-subtitle">Most recent paid order</div>
                  </div>
                </div>
              )}

              {(approvalStatus === BuyApprovalStatusEnum.Pending ||
                approvalStatus === BuyApprovalStatusEnum.Rejected ||
                approvalStatus === BuyApprovalStatusEnum.Approved) && (
                <div className="store-analytics-detail__actions">
                  {approvalStatus !== BuyApprovalStatusEnum.Approved && (
                    <button
                      className="update-btn"
                      disabled={isReviewSaving}
                      onClick={() => confirmApprove(store._id, store.name)}
                    >
                      Approve for BUY
                    </button>
                  )}
                  {approvalStatus !== BuyApprovalStatusEnum.Rejected && (
                    <button
                      className="cancel-btn"
                      disabled={isReviewSaving}
                      onClick={() =>
                        openReject({
                          _id: store._id,
                          name: store.name,
                          isLive: approvalStatus === BuyApprovalStatusEnum.Approved,
                        })
                      }
                    >
                      {approvalStatus === BuyApprovalStatusEnum.Approved
                        ? "Take off BUY"
                        : "Reject"}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
              This store has not applied to sell on BUY.
            </div>
          )}
        </div>

        <div className="store-analytics-detail__card store-analytics-detail__card--full">
          <h2>Performance Analytics</h2>
          <div className="store-analytics-detail__metrics">
            <div className="store-analytics-detail__metric">
              <div className="metric-label">Total Active Days</div>
              <div className="metric-value">{analytics.totalActiveDays}</div>
              <div className="metric-subtitle">Days with sales</div>
            </div>

            <div className="store-analytics-detail__metric store-analytics-detail__metric--success">
              <div className="metric-label">Total Sales</div>
              <div className="metric-value">
                {formatCurrency(analytics.totalSales)}
              </div>
              <div className="metric-subtitle">All-time revenue</div>
            </div>

            <div className="store-analytics-detail__metric">
              <div className="metric-label">Total Bills</div>
              <div className="metric-value">{analytics.totalBills}</div>
              <div className="metric-subtitle">All-time orders</div>
            </div>

            <div className="store-analytics-detail__metric store-analytics-detail__metric--warning">
              <div className="metric-label">Last 7 Days Sales</div>
              <div className="metric-value">
                {formatCurrency(analytics.last7DaysSales)}
              </div>
              <div className="metric-subtitle">
                {analytics.last7DaysBills} bills
              </div>
            </div>

            <div className="store-analytics-detail__metric store-analytics-detail__metric--warning">
              <div className="metric-label">Last 30 Days Sales</div>
              <div className="metric-value">
                {formatCurrency(analytics.last30DaysSales)}
              </div>
              <div className="metric-subtitle">
                {analytics.last30DaysBills} bills
              </div>
            </div>

            <div className="store-analytics-detail__metric">
              <div className="metric-label">Last Active</div>
              <div className="metric-value" style={{ fontSize: "20px" }}>
                {analytics.lastActiveDate ? formatDate(analytics.lastActiveDate) : "N/A"}
              </div>
              <div className="metric-subtitle">
                First active: {analytics.firstDaybookDate ? formatDate(analytics.firstDaybookDate) : "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>
      {rejectModal}
    </div>
  );
};

export default StoreAnalyticsDetail;
